/* global Office */
/* global Word */

import React, { useCallback } from "react";
import toast from "react-hot-toast";

import { useSuggestedRedlinesStyles } from "./styles";

import { RedlineCard } from "@/components/suggested-redlines/components/redline-card";
import { RedlineApplied } from "@/components/suggested-redlines/components/redline-applied";
import { Button, Caption1 } from "@fluentui/react-components";
import { useRedlinesContext } from "@/context/redlines";
import { Play24Regular } from "@fluentui/react-icons";
import { useDocumentClassification } from "@/context/document-classification";
import { DocumentClassification, Redline, RedlinePiece } from "@/types/risk-analysis";
import { useAuth } from "@/context/auth";
import { runAnalyzeRedline } from "@/services/risk-analysis-service";
import log from "@/utils/logger";
import { getParagraph } from "@/taskpane/lib/common/search";
export interface SuggestedRedlinesProps {
  rule: DocumentClassification["rules"][number];
  onRefreshAnalysis?: () => void;
}

export const SuggestedRedlines: React.FC<SuggestedRedlinesProps> = ({ rule, onRefreshAnalysis }) => {
  const styles = useSuggestedRedlinesStyles();
  const { selectedPlaybook, refreshAnalysis, selectedPromptConfig } = useDocumentClassification();
  const { user } = useAuth();
  const {
    filteredRedlines,
    openItems,
    handleApplyRedlines,
    handleShowRedlines,
    handleToggle,
    loadingRedlines,
    errorRedlines,
    appliedRedlines,
    stopRedlineLoading,
    setRedlineInfoMessage,
    setRedlineErrorMessage,
    infoRedlines,
    updateRedline,
    markRedlineAsReanalyzed,
    clearAllReanalyzedRedlines,
  } = useRedlinesContext();

  const handleRefreshAnalysis = useCallback(async () => {
    clearAllReanalyzedRedlines();
    onRefreshAnalysis?.();
    await refreshAnalysis(rule.id);
  }, [clearAllReanalyzedRedlines, onRefreshAnalysis, refreshAnalysis, rule.id]);

  const getAppliedRedlineById = useCallback(
    (paragraphId: string, pieceId: string) => {
      const pieceChanged = appliedRedlines?.[rule.clauseTopic]
        ?.find((appliedRedline) => appliedRedline.paragraphId === paragraphId)
        ?.pieces.find((piece) => piece.id === pieceId);
      return pieceChanged;
    },
    [appliedRedlines, rule.clauseTopic]
  );

  /**
   * Attempts to apply a redline suggestion to a specific piece of content.
   *
   * If the application fails due to content changes (detected by a "new-content" error),
   * it re-analyzes the content and proposes an updated redline suggestion. If the re-analysis
   * succeeds without errors, it updates the redline and notifies the user to review the new suggestion.
   * If errors occur during re-analysis, an error message is shown. All errors are logged.
   *
   * @param redline - The redline suggestion to apply.
   * @param piece - The specific piece of content to which the redline should be applied.
   * @param pieceId - The unique identifier for the piece.
   * @param promptConfigId - The identifier for the prompt configuration.
   * @param userId - The identifier of the user performing the action.
   * @returns A Promise that resolves when the redline application process is complete.
   */
  const applyRedlines = async (
    redline: Redline,
    piece: RedlinePiece,
    pieceId: string,
    promptConfigId: string,
    userId: string
  ) => {
    try {
      // Validate required dependencies early
      if (!selectedPlaybook || !selectedPromptConfig || !user?.user?.id) {
        log.error("Missing required dependencies", "applyRedlines", {
          hasSelectedPlaybook: !!selectedPlaybook,
          hasSelectedPromptConfig: !!selectedPromptConfig,
          hasUserId: !!user?.user?.id,
        });
        setRedlineErrorMessage(pieceId, {
          type: "failed-to-review",
          message: "Missing required configuration. Please refresh the page.",
        });
        return;
      }

      log.debug("applyRedlines called", "applyRedlines", { redline, piece, pieceId, promptConfigId, userId });

      const result = await handleApplyRedlines(redline, piece, pieceId, promptConfigId, userId);
      log.debug("Result from handleApplyRedlines", "applyRedlines", result);

      if (!result.success && result.error.type === "new-content" && !!result.data) {
        log.debug("New content detected, re-analyzing", "applyRedlines", {
          error: result.error,
          data: result.data,
        });

        await handleContentChangedReanalysis(redline, piece, pieceId, result.data);
      }
    } catch (error) {
      log.error("Error in applyRedlines", "applyRedlines", error);
      setRedlineErrorMessage(pieceId, {
        type: "failed-to-review",
        message: "An unexpected error occurred. Please try again.",
      });
      stopRedlineLoading(pieceId);
    }
  };

  /**
   * Handles re-analysis when content has changed
   */
  const handleContentChangedReanalysis = async (
    redline: Redline,
    piece: RedlinePiece,
    pieceId: string,
    resultData: any
  ) => {
    try {
      const playbookRule = selectedPlaybook.rules.find((r) => r.id === rule.id);
      if (!playbookRule) {
        throw new Error("Playbook rule not found");
      }

      // Clear any existing error states before starting re-analysis
      setRedlineErrorMessage(pieceId, undefined);

      const newRedlinePromise = runAnalyzeRedline(
        resultData.selectedRuleId,
        playbookRule.selectedCustomerRuleId ?? "",
        resultData.promptConfigId,
        resultData.userId,
        resultData.paragraphs,
        resultData.piece
      );

      const newRedline = await toast.promise(newRedlinePromise, {
        loading: "New content detected, re-analyzing",
        success: "Redline updated. Please review before applying",
        error: "Failed to re-analyze redline",
      });

      log.debug("Result from runAnalyzeRedline", "applyRedlines", newRedline);

      if (!newRedline?.errors?.length) {
        await updateRedlineWithNewContent(redline, piece, pieceId, newRedline);
      } else {
        // Errors found in new redline analysis
        log.debug("Errors found in new redline analysis", "applyRedlines", newRedline.errors);
        setRedlineErrorMessage(pieceId, {
          type: "failed-to-review",
          message: "Failed to review the redline due to analysis errors.",
        });
        stopRedlineLoading(pieceId);
      }
    } catch (error: any) {
      log.error("Error in content changed re-analysis", "applyRedlines", error);
      setRedlineErrorMessage(pieceId, {
        type: "failed-to-review",
        message:
          error.message === "Re-analysis timeout"
            ? "Re-analysis timed out. Please try again."
            : "Failed to review the redline.",
      });
      stopRedlineLoading(pieceId);
    }
  };

  /**
   * Updates the redline with new content from re-analysis
   */
  const updateRedlineWithNewContent = async (
    redline: Redline,
    _piece: RedlinePiece,
    pieceId: string,
    newRedline: any
  ) => {
    try {
      // Set info message and stop loading first
      setRedlineInfoMessage(pieceId, {
        type: "redline-updated",
        message: "Content has changed, please review the updated redline before applying.",
      });
      stopRedlineLoading(pieceId);

      // Ensure Office is ready before proceeding
      await Office.onReady();

      await Word.run(async (context) => {
        const paragraph = await getParagraph({
          context,
          paragraphId: redline.originalChunk.id,
          paragraph: redline.originalChunk._paragraph,
        });

        if (!paragraph) {
          throw new Error("Failed to get paragraph");
        }

        paragraph._paragraph.load("text");
        await paragraph._paragraph.context.sync();

        const currentText = paragraph._paragraph.text;

        // Update the redline with new content
        updateRedline({
          originalChunk: {
            _paragraph: paragraph,
            id: paragraph.id,
            text: currentText,
            fullText: currentText,
          },
          pieces: redline.pieces.map((p) => {
            if (p.id === pieceId) {
              log.debug("Updating piece with new redline", "applyRedlines", {
                pieceId,
                updatedPiece: newRedline.result.updatedPiece,
              });
              return {
                ...p,
                ...newRedline.result.updatedPiece,
              };
            }
            return p;
          }),
        });

        // Clear error state and mark as re-analyzed (atomic operations)
        setRedlineErrorMessage(pieceId, undefined);
        markRedlineAsReanalyzed(pieceId);
      });
    } catch (error) {
      log.error("Error updating redline with new content", "applyRedlines", error);
      setRedlineErrorMessage(pieceId, {
        type: "failed-to-review",
        message: "Failed to update redline content.",
      });
      stopRedlineLoading(pieceId);
    }
  };

  return (
    <div className={styles.fullReportListItem}>
      {!filteredRedlines[rule.clauseTopic]?.length && !appliedRedlines?.[rule.clauseTopic]?.length ? (
        <span>No redlines needed</span>
      ) : (
        <>
          {filteredRedlines[rule.clauseTopic].map((redline, index) => (
            <div key={`redline-${redline.originalChunk?.id}-${index}`}>
              {redline?.pieces?.length === 0 && <span>No redlines needed</span>}
              {redline?.pieces?.length > 0 &&
                redline?.pieces?.map((piece, pieceIndex) => (
                  <div key={`${piece.originalSentence}-${pieceIndex}`} className={styles.redlineCard}>
                    {getAppliedRedlineById(redline.originalChunk.id, piece.id) ? (
                      <RedlineApplied
                        pieceId={piece.id}
                        paragraphId={redline.originalChunk.id}
                        openItems={openItems}
                        handleToggle={handleToggle}
                        getAppliedRedlineById={getAppliedRedlineById}
                      />
                    ) : (
                      <RedlineCard
                        piece={piece}
                        openItems={openItems}
                        handleToggle={handleToggle}
                        handleShowRedlines={() => handleShowRedlines(redline, piece, pieceIndex)}
                        handleApplyRedlines={() =>
                          applyRedlines(redline, piece, piece.id, selectedPromptConfig?.id ?? "", user?.user?.id ?? "")
                        }
                        isLoading={loadingRedlines[piece.id]}
                        error={errorRedlines[piece.id]}
                      />
                    )}
                    {errorRedlines[piece.id] && (
                      <div className={styles.additionalRow}>
                        <Caption1 className={styles.redlineErrorMessage}>{errorRedlines[piece.id].message}</Caption1>
                        {errorRedlines[piece.id].type === "new-content" && (
                          <Button
                            size="small"
                            appearance="primary"
                            onClick={handleRefreshAnalysis}
                            icon={<Play24Regular />}
                          />
                        )}
                      </div>
                    )}
                    {infoRedlines[piece.id] && (
                      <div className={styles.additionalRow}>
                        <Caption1 className={styles.redlineInfoMessage}>{infoRedlines[piece.id].message}</Caption1>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          ))}
        </>
      )}
    </div>
  );
};
